import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from "@angular/material/legacy-dialog";
import { logger } from "@app/_helpers/logger";
import { ConfirmDialogComponent } from "@app/components/generic-dialogs/confirm-dialog/confirm-dialog.component";
import { environment } from "@env/environment";
import { TranslateService } from "@ngx-translate/core";
import { Logger } from "@timeghost/web-api";
import { fromEvent, merge, of, Subject, Subscription, timer } from "rxjs";
import { catchError, exhaustMap, map, takeUntil } from "rxjs/operators";

const log = new Logger("VersionCheckService");
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const versionJsonUrl = "/assets/version.json";
interface VersionPayload {
	version?: string | number;
}

const getCurrentAppVersionFromReleaseName = () => {
	const releaseName = environment.releaseName?.split("Build ")[1];
	if (!releaseName) return null;
	if (/^\d+$/g.test(releaseName)) return Number(releaseName);
	return null;
};

@Injectable({ providedIn: "root" })
export class VersionCheckService implements OnDestroy {
	private readonly destroy$ = new Subject<void>();
	private checkSubscription?: Subscription;
	private isDisabled = false;
	private currentVersion: number | null = environment.production ? getCurrentAppVersionFromReleaseName() : 0;
	private latestPromptedVersion: number | null = null;
	private dialogRef?: MatDialogRef<ConfirmDialogComponent>;

	constructor(
		private readonly httpClient: HttpClient,
		private readonly translateService: TranslateService,
		private readonly dialog: MatDialog
	) {}

	start(): void {
		if (this.checkSubscription || this.isDisabled) return;

		this.checkSubscription = merge(timer(0, CHECK_INTERVAL_MS), fromWindowFocusEvent())
			.pipe(
				takeUntil(this.destroy$),
				exhaustMap(() =>
					this.fetchVersion().pipe(
						catchError((error: unknown) => {
							this.handleFetchError(error);
							return of(null);
						})
					)
				),
				map((version) => (version !== null ? version : null))
			)
			.subscribe((serverVersion) => {
				if (serverVersion === null || this.isDisabled) return;
				this.handleFetchedVersion(serverVersion);
			});
	}

	stop(): void {
		this.destroy$.next();
		this.checkSubscription?.unsubscribe();
		this.checkSubscription = undefined;
	}

	ngOnDestroy(): void {
		this.stop();
		this.destroy$.complete();
	}

	private fetchVersion() {
		const cacheBypass = Date.now();
		return this.httpClient
			.get<VersionPayload>(
				new URL(versionJsonUrl, window.location.origin).toString() +
					"?" +
					new URLSearchParams({ ts: cacheBypass.toString() }).toString()
			)
			.pipe(map((payload) => parseVersionValue(payload?.version)));
	}

	private handleFetchedVersion(serverVersion: number): void {
		if (this.currentVersion === null) {
			this.currentVersion = serverVersion;
			return;
		}
		logger.debug("version check result", {
			currentVersion: this.currentVersion,
			latestVersion: serverVersion,
		});

		if (serverVersion <= this.currentVersion) return;

		if (this.latestPromptedVersion === serverVersion && this.dialogRef) return;

		this.currentVersion = serverVersion;
		this.latestPromptedVersion = serverVersion;
		this.openUpdateDialog();
	}

	private handleFetchError(error: unknown): void {
		if (!(error instanceof HttpErrorResponse)) {
			log.warn("version check failed", error);
			return;
		}

		if (error.status === 404) {
			this.isDisabled = true;
			this.stop();
			log.warn("version file not found. disabling version checks.");
			return;
		}

		log.warn("version check request failed", error.status, error.message);
	}

	private async openUpdateDialog(): Promise<void> {
		if (this.dialogRef) return;
		const strings = {
			title: "utils.update-available.title",
			icon: "update",
			text: "utils.update-available.text",
			confirm: "utils.update-available.ok",
			cancel: "utils.update-available.cancel",
		};
		const title = this.translateService.instant(strings.title) as string;
		const text = this.translateService.instant(strings.text) as string;
		this.dialogRef = this.dialog.open(ConfirmDialogComponent, {
			disableClose: true,
			closeOnNavigation: false,
			width: "460px",
			data: {
				title,
				icon: strings.icon,
				text,
				strings: strings,
			},
		});

		this.dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((shouldReload) => {
				this.dialogRef = undefined;
				if (shouldReload === true) window.location.reload();
			});
	}
}

function fromWindowFocusEvent() {
	return fromEvent(window, "focus");
}

function parseVersionValue(version: VersionPayload["version"]): number {
	const parsedVersion = Number(version);
	if (!Number.isFinite(parsedVersion)) {
		throw new Error("Invalid version payload");
	}
	return parsedVersion;
}
